'use client';

import { Button as AntButton } from 'antd';
import React from 'react';

import { AppColors } from '../../theme';
import styles from './Button.style';

/**
 * Props para el componente Button.
 */
type ButtonProps = {
  /**
   * Maneja el evento de clic.
   */
  onClick: () => void;
  /**
   * Determina si el botón está deshabilitado.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * El contenido del botón.
   */
  children?: React.ReactNode;
  /**
   * El tipo de botón.
   *
   * @default 'primary'
   */
  type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
  /**
   * Estilos personalizados para el botón.
   */
  style?: React.CSSProperties;
};

/**
 * Un componente de botón personalizado que envuelve el Botón de Ant Design con estilos específicos.
 *
 * @param {ButtonProps} props - Las props para el componente Button.
 *
 * @returns {JSX.Element} El componente Button renderizado.
 */
export function Button({
  onClick,
  disabled = false,
  children,
  type = 'primary',
  style
}: ButtonProps) {
  const getButtonStyles = () => {
    const baseStyles = { ...styles.button };
    if (type === 'link') {
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        border: 'none',
        padding: '0',
        minWidth: 'auto',
        height: 'auto'
      };
    }

    return baseStyles;
  };

  const getDisabledStyles = () => {
    if (!disabled) return {};

    if (type === 'link') {
      return {
        color: AppColors.VA.NEUTRALS._60_,
        cursor: 'not-allowed',
        backgroundColor: 'transparent'
      };
    }

    return styles.buttonDisabled;
  };

  return (
    <AntButton
      type={type}
      onClick={onClick}
      disabled={disabled}
      variant={disabled ? 'outlined' : undefined}
      style={{
        ...getButtonStyles(),
        ...style,
        ...getDisabledStyles()
      }}
      onMouseEnter={(e) => {
        if (!disabled && type !== 'link') {
          Object.assign(e.currentTarget.style, styles.buttonHover);
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && type !== 'link') {
          Object.assign(e.currentTarget.style, {
            backgroundColor: AppColors.VA.PRIMARY._50_,
            transform: 'none',
            boxShadow: 'none'
          });
        }
      }}>
      {children}
    </AntButton>
  );
}
export default Button;
